import {
  readInstantiatedFrom,
  resolveTemplateRegistryEntry,
} from '@/features/templates/content-form/project-lineage'

export type StudioInteractionTab = 'chat' | 'context' | 'content'

export interface StudioInteractionCapabilities {
  hasProjectConversation: boolean
  /**
   * The project carries template provenance
   * (`settings.cogoApplicationMetadata.instantiatedFrom`) — it was seeded
   * from SOME template, registered or not. This is the provenance FACT; it
   * says nothing about whether any template surface can render.
   */
  hasTemplateLineage: boolean
  /**
   * The project is an instance of a REGISTERED (`TEMPLATE_REGISTRY`)
   * `cogo.template.v1` package — i.e. `TemplateContentPanel` has a package
   * whose slot declarations it can build the content form from.
   *
   * This is deliberately stricter than `hasTemplateLineage`:
   * `TemplateContentPanel` renders `null` for lineage whose `templateId` has
   * no registry entry, so gating the 콘텐츠 tab on lineage ALONE would ship a
   * permanently empty tab for every project seeded from an unregistered
   * template (the whole Starters catalog — `fitness-gym-new3` and its nine
   * siblings — which is disjoint from `TEMPLATE_REGISTRY`). Registering
   * those templates as packages is what flips this to true for them; until
   * then the tab correctly stays hidden rather than dead.
   */
  hasTemplateContent: boolean
  tabs: StudioInteractionTab[]
}

interface StudioInteractionCapabilityInput {
  currentProjectId: string | null
  currentProject: unknown
  projectMetadata: Record<string, unknown> | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function readProjectPayloadMetadata(
  currentProject: unknown,
): Record<string, unknown> | null {
  const project = asRecord(currentProject)
  const direct = asRecord(project?.metadata)
  if (direct) return direct

  const manifest = asRecord(project?.manifest)
  return asRecord(manifest?.metadata)
}

export function deriveStudioInteractionCapabilities({
  currentProjectId,
  currentProject,
  projectMetadata,
}: StudioInteractionCapabilityInput): StudioInteractionCapabilities {
  const lineage =
    readInstantiatedFrom(projectMetadata) ??
    readInstantiatedFrom(readProjectPayloadMetadata(currentProject))
  const hasTemplateLineage = Boolean(lineage)
  const hasTemplateContent = Boolean(resolveTemplateRegistryEntry(lineage))

  return {
    hasProjectConversation: Boolean(currentProjectId || currentProject),
    hasTemplateLineage,
    hasTemplateContent,
    tabs: hasTemplateContent
      ? ['chat', 'context', 'content']
      : ['chat', 'context'],
  }
}
